import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { Crisp } from 'crisp-sdk-web';
import { environment } from '@env/environment';
import { svgIconList } from '@shared/svg-icon-list';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { ContactList } from '@shared/models/contact-list.model';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { SiteHttpService } from '@core/services/site-http.service';
import { SupportChatService } from '@core/services/support-chat.service';

declare const window: any;

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [CommonModule, TranslateModule, SafeHtmlPipe],
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.scss',
})
export class ContactUsComponent implements OnInit, OnDestroy {

  contactList: ContactList[] = [];
  isSkeletonContactList = true;

  svg: any = svgIconList;
  isQP20 = ['qp20', 'qp20-dark'].includes(environment.projectCode.toLowerCase());

  private readonly isBrowser: boolean;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private eventEmitterService: EventEmitterService,
    private siteHttpService: SiteHttpService,
    private supportChatService: SupportChatService,
    private renderer: Renderer2,
    private router: Router,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    /* Tag <body> on server and browser alike so the scss override drops
       base-inner's default-height (100vh) for this page from the first paint.
       The footer is position:relative (normal flow), so with no height floor
       it simply hugs the content — same as template-02, where this route is in
       base-inner's isContentPage exclude list. Any vh-based floor here inflates
       when the browser zooms out and shows up as a giant gap above the footer. */
    this.renderer.addClass(this.document.body, 'contact-us-loaded');

    if (!this.isBrowser) return;

    /* The contact list is prefetched by app.component (getContactUs) into
       sessionStorage; the emitter fires once the fetch lands, covering direct
       navigation to this page before the prefetch completes. */
    const cached = sessionStorage.getItem('contact_us');
    if (cached !== null) {
      this.contactList = JSON.parse(cached);
      this.isSkeletonContactList = false;
    }

    this.eventEmitterService.updateContactUsEmitter
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.contactList = JSON.parse(sessionStorage.getItem('contact_us')) ?? [];
        this.isSkeletonContactList = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    /* Leaving the page restores default-height to its normal 100vh everywhere else. */
    this.renderer.removeClass(this.document.body, 'contact-us-loaded');
  }

  hasType(type: string): boolean {
    return this.contactList.some(item => item.contact_type === type);
  }

  onContact(link: string) {
    if (link !== undefined && link !== null) {
      if (link.includes('external')) {
        this.router.navigate([]).then(() => { window.open(link, '_blank'); });
      } else {
        window.open(link, '_blank');
      }
    }
  }

  /**
   * AIO-82885: with Skynet on, `siteHttpService.website` is never populated — only
   * /livechat/getscript fills it, and that is never called — so the old body fell through to
   * an empty `chat_page` and this link did nothing at all. SupportChatService decides, and
   * the live-chat body below is unchanged for the brands still on it.
   */
  openLiveChat() {
    this.supportChatService.open(() => {
      if (this.siteHttpService.website !== null && this.siteHttpService.website !== '') {
        if (this.siteHttpService.live_chat_platform_name == 'Live Chat') {
          window.LiveChatWidget.call('maximize');
        } else if (this.siteHttpService.live_chat_platform_name == 'Crisp') {
          Crisp.chat.open();
        }
      } else if (this.siteHttpService.chat_page !== null && this.siteHttpService.chat_page !== '') {
        window.open(this.siteHttpService.chat_page, '_blank');
      }
    });
  }
}
